import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { Link, RouteComponentProps } from 'react-router-dom';

import { Breadcrumb, Button, Card, Col, Row, Typography } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoneyBillAlt } from '@fortawesome/free-solid-svg-icons';
import { getEcole } from '../ecole/ecole.reducer';
import { getSession } from 'app/shared/reducers/authentication';
import { getFacture, getFrais, partialUpdateFacture, solderFacture } from '../factures/facture.reducer';
import SoldActionModal from 'app/entities/scolarite/SoldActionModal';
import GenericActionModal from 'app/entities/scolarite/GenericActionModal';
import DetailsFacture from './DetailsFacture';
import { MoyenPaiement, StatutFacture } from 'app/shared/model/facture.model';
import { useModal } from 'app/hooks/useModal';
import './facture-details.scss';
import { solder } from '../payments/payment.reducer';
import { useCheckIsAnneeInactive } from 'app/hooks/useCheckIsAnneeActive';
import { StatusAnneeScolaire } from 'app/shared/model/anneeScolaire.model';

const { Title } = Typography;

const FactureDetail = ({ match }: RouteComponentProps<{ id: string; ecoleid: string; anneeScolaireId: string }>) => {
  const dispatch = useAppDispatch();
  const anneeScolaireId = match.params.anneeScolaireId;
  const anneeScolaire = useAppSelector(state => state.anneeScolaire.entity);
  const facture = useAppSelector(state => state.facture.entity);
  const frais = useAppSelector(state => state.facture.frais);
  const factureLoading = useAppSelector(state => state.facture.loading);
  const inactiveAnnee = useCheckIsAnneeInactive(anneeScolaireId);
  const currentEcole = useAppSelector(state => state.ecole.entity);
  const [editingMontantNet, setEditingMontantNet] = useState(false);
  const [editingStatut, setEditingStatut] = useState(false);
  const [montantNet, setMontantNet] = useState(facture.montant);
  const [statutFacture, setStatutFacture] = useState(facture.statutFacture);

  const [modalVisible, showModal, hideModal] = useModal();
  const [processing, setProcessing] = useState(false);
  const [genericModalData, setGenericModalData] = useState({
    visible: false,
    initialValues: {
      name: ``,
      amount: 0,
      fees: 0,
      totalAmount: 0,
      phoneNumber: '',
      type: MoyenPaiement.ESPECE_CHECK,
    },
  });

  useEffect(() => {
    dispatch(getSession());
    dispatch(getFacture(match.params.id));
    dispatch(getFrais(match.params.id));
    dispatch(getEcole(match.params.ecoleid));
  }, [dispatch, match.params.id, match.params.ecoleid]);

  function handleGenericAction(type: MoyenPaiement) {
    setGenericModalData({
      visible: true,
      initialValues: {
        name: `${facture.eleve}`,
        amount: facture.montant || 0,
        fees: frais,
        totalAmount: facture.montant + Number(frais),
        phoneNumber: `${facture.telephone}` || '',
        type,
      },
    });
  }

  const handleGenericModalCancel = () => {
    setGenericModalData({ visible: false, initialValues: genericModalData.initialValues });
  };

  const handleGenericModalConfirm = async (values: {
    name: string;
    amount: number;
    fees: number;
    totalAmount: number;
    phoneNumber: string;
    type: MoyenPaiement;
  }) => {
    try {
      const payload = { factureId: match.params.id, phoneNumber: values.phoneNumber, moyenPaiement: genericModalData.initialValues.type };
      await dispatch(solder(payload));
      handleGenericModalCancel();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  function handleSold() {
    showModal();
  }

  const handleMontantEdit = () => {
    if (editingMontantNet === false) {
      setEditingMontantNet(true);
      setMontantNet(facture.montant);
    } else {
      setEditingMontantNet(false);
    }
  };

  const handleStatutEdit = () => {
    if (editingStatut === false) {
      setEditingStatut(true);
      setStatutFacture(facture.statutFacture);
    } else {
      setEditingStatut(false);
    }
  };

  const handleModalCancel = () => {
    hideModal();
  };

  const handleModalConfirm = () => {
    setProcessing(true);
    dispatch(solderFacture(match.params.id));
    hideModal();
    setProcessing(false);
  };

  const handleConfirm = () => {
    setProcessing(true);
    const updatedFacture = { ...facture, id: match.params.id };
    if (editingMontantNet) {
      updatedFacture.montant = montantNet;
    }
    if (editingStatut) {
      updatedFacture.statutFacture = statutFacture;
    }
    dispatch(partialUpdateFacture(updatedFacture));
    setEditingMontantNet(false);
    setEditingStatut(false);
    setProcessing(false);
  };

  const buttonIcons = [
    {
      method: 'WAVE',
      icon: <img src="content/images/wave.png" alt="wave" />,
      bgColor: '#1cc8ff',
      action: () => handleGenericAction(MoyenPaiement.WAVE),
    },
    {
      method: 'OM',
      icon: <img src="content/images/om.png" alt="om" />,
      bgColor: '#fff',
      action: () => handleGenericAction(MoyenPaiement.OM),
    },
    { method: 'Espèces/Chèque', icon: <FontAwesomeIcon size="4x" icon={faMoneyBillAlt} />, bgColor: 'whitesmoke', action: handleSold },
  ];

  const handleDisable = () =>
    facture.statutFacture === StatutFacture.EN_ATTENTE ||
    facture.statutFacture === StatutFacture.PAYEE ||
    (inactiveAnnee && anneeScolaire.statutAnneeScolaire !== StatusAnneeScolaire.INACTIVE);
  return (
    <div className="cnt">
      <Breadcrumb>
        <Breadcrumb.Item>
          <Link to={`/home/${currentEcole?.id}`}>{currentEcole?.nom}</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link to={`/scolarite/${currentEcole?.id}/${anneeScolaireId}`}>Facture</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link to={`${match.url}`}>Détails</Link>
        </Breadcrumb.Item>
      </Breadcrumb>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={18}>
          <Card>
            <div className="invoice-header">
              <DetailsFacture
                facture={facture}
                frais={frais}
                anneeScolaireId={anneeScolaireId}
                editingStatut={editingStatut}
                handleConfirm={handleConfirm}
                handleMontantEdit={handleMontantEdit}
                handleStatutEdit={handleStatutEdit}
                montantNet={montantNet}
                setMontantNet={setMontantNet}
                statutFacture={statutFacture}
                setStatutFacture={setStatutFacture}
                editingMontantNet={editingMontantNet}
                inactiveAnnee={inactiveAnnee}
                handleDisable={handleDisable}
              />
            </div>
            <SoldActionModal
              visible={modalVisible}
              onCancel={handleModalCancel}
              handleModalOk={handleModalConfirm}
              confirmLoading={processing}
            />
            <GenericActionModal
              visible={genericModalData.visible}
              onCancel={handleGenericModalCancel}
              handleModalOk={handleGenericModalConfirm}
              confirmLoading={factureLoading}
              initialValues={genericModalData.initialValues}
            />
          </Card>
        </Col>
        <Col xs={24} sm={24} md={6}>
          <Card>
            <div className="options">
              <Title level={3}>Solder Par</Title>
              <div className="button-group">
                {buttonIcons.map((btn, index) => (
                  <Button
                    key={index}
                    style={{ background: btn.bgColor, marginBottom: 10 }}
                    disabled={handleDisable()}
                    type="text"
                    size="large"
                    onClick={btn.action}
                  >
                    {btn.icon} {btn.method === 'Espèces/Chèque' && btn.method}
                  </Button>
                ))}
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default FactureDetail;
